import React from 'react';
import { useQuery, gql } from '@apollo/client';
import { SiteSettings } from 'src/scripts/utils/environment';
import PixleeAnalytics from '../../../utils/pixlee-analytics';

import './../../_shared/ui-components/PairsGreatWith/styles.scss';
import { PixleeAlbum } from './pixlee-types';
import { getOnlineSalePricing, heapTrack } from 'src/scripts/utils/helpers';
import PixleePost from './PixleePost';
import SwiperCore, { A11y, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper.scss';
import 'swiper/components/navigation/navigation.scss';
import { ShippingCountryState } from '../../../utils/shipping';
import { observer } from 'mobx-react-lite';

SwiperCore.use([A11y, Navigation]);

interface IPixleeFeedContainer {
  handles: string[];
  pixleeData: PixleeAlbum[];
  albumId: number | undefined;
  isPDP: boolean;
  title: string;
  isPageLayout: boolean;
  viewGrid: boolean;
  isMobile: boolean;
  viewThePost: (postId: number) => void;
}

let PixleeFeedContainer = ({
  handles,
  pixleeData,
  albumId,
  title,
  isPDP,
  isPageLayout,
  viewGrid,
  isMobile,
  viewThePost,
}: IPixleeFeedContainer) => {
  const sendPixleeAnalyticsOnRightScroll = () => {
    try {
      PixleeAnalytics.sendDataOnRightScroll({ albumId: albumId?.toString() });
    } catch (error) {
      console.log(error);
    }
  };

  const sendHeapTrackingEvent = (name: string) => {
    try {
      heapTrack(name, {});
    } catch (error) {
      console.log(error);
    }
  };

  const GET_PRODUCTS_AVAILABILITY = (handles: string[]) => {
    const queryBody = handles
      .map(
        (handle, index) =>
          `
        _${index}: productByHandle(handle: "${handle}") {
          id
          onlineStoreUrl
          availableForSale
          handle
          tags
          variants(first: 1) {
            nodes {
              price {
                amount
                currencyCode
              }
            }
          }
        }
      `
      )
      .join('');
    return gql`
      query GetProductsAvailabilityQuery($countryCode: CountryCode) @inContext(country: $countryCode) {
        ${queryBody}
      }
    `;
  };

  const getProductsQuery = GET_PRODUCTS_AVAILABILITY(handles);
  const currentCountry = ShippingCountryState.countryCode || 'US';

  const { loading, error, data: dataGQL } = useQuery(getProductsQuery, {
    skip: handles.length === 0,
    variables: { countryCode: currentCountry },
  });
  if (error) {
    console.log(error);
  }

  // ? [REVISIT] this component needs other clean; when it was written some things were not possible but are now (tooling, browser versions + library versions, that some of this can be done better/removed)
  // -- not going to touch it now before holiday freeze
  if (!loading) {
    var productsShopifyData: {
      [key: string]: {
        availableForSale: boolean;
        onlineStoreUrl: string;
        onlineSalePrice: number | undefined;
        shopifyPrice: number;
      };
    } = {}; // Keeping it as var since we need it outside
    for (let [, value] of Object.entries(dataGQL)) {
      if (value) {
        //@ts-expect-error // ? [REVISIT]
        productsShopifyData[`${value.handle.replace(/-/g, '_').toString()}`] = {
          //@ts-expect-error // ? [REVISIT]
          availableForSale: value.availableForSale,
          //@ts-expect-error // ? [REVISIT]
          onlineStoreUrl: value.onlineStoreUrl,
          //@ts-expect-error // ? [REVISIT]
          onlineSalePrice: getOnlineSalePricing(value.tags, undefined, true),
          //@ts-expect-error // ? [REVISIT]
          shopifyPrice: Number(value.variants.nodes[0].price.amount),
        };
      }
    }
  }

  const fineTunningByBreakpoint = {
    1450: {
      slidesPerView: 4.5,
    },
    1100: {
      slidesPerView: 3.5,
    },
    740: {
      slidesPerView: 2.2,
    },
    525: {
      slidesPerView: 1.5,
    },
    1: {
      slidesPerView: 1.5,
    },
  };

  const validPixleeData =
    SiteSettings?.pixleeShouldHideUnpublishedProducts ?? false
      ? pixleeData
          ?.map((post) => {
            if (!productsShopifyData) {
              return { ...post, products: [] };
            }
            const validProducts = post.products.filter((product) => {
              const productSkuUnderscore = product.sku?.replace(/-/g, '_');
              const matchingProductData = productsShopifyData[productSkuUnderscore];

              return (
                matchingProductData?.onlineStoreUrl !== null && typeof matchingProductData?.onlineStoreUrl === 'string'
              );
            });
            return { ...post, products: validProducts };
          })
          .filter((post) => post.products.length > 0)
      : pixleeData;

  // if we get to 0 valid results should we just throw & EB hide whole container (vs just this section)
  // -- not going to touch it now before holiday freeze & it's highly unlikely for 0 product to be valid

  if (loading || validPixleeData.length === 0) return null;

  return !isPageLayout ? (
    <Swiper
      spaceBetween={15}
      slidesPerView={4.5}
      navigation={validPixleeData?.length >= 3}
      breakpoints={fineTunningByBreakpoint}
      allowTouchMove
      a11y={{
        prevSlideMessage: 'Back to previous posts',
        nextSlideMessage: 'More posts',
      }}
      speed={200}
      onTouchEnd={(slide) => {
        sendHeapTrackingEvent(`Swipe through ${title}`);
        // @ts-expect-error
        if (slide.swipeDirection === 'next') {
          sendPixleeAnalyticsOnRightScroll();
        }
      }}
      onSlideChange={(slide) => {
        sendHeapTrackingEvent(`Swipe through ${title}`);
        if (slide.realIndex > slide.previousIndex) {
          sendPixleeAnalyticsOnRightScroll();
        }
      }}
    >
      {validPixleeData?.map((post, index) => (
        <SwiperSlide key={post.album_photo_id} zoom>
          <PixleePost data={post} key={post.id} index={index} productsShopifyData={productsShopifyData} />
        </SwiperSlide>
      ))}
      {!isPDP && (
        <SwiperSlide>
          <div className={`post`}>
            <a href="/pages/inspiration" className={`post__image-wrapper`}>
              <img
                className={`img-fluid post__image`}
                src="https://cdn.shopify.com/s/files/1/2185/2813/files/view-more_2.png?v=1700252212"
                alt="View More"
              />
            </a>
          </div>
        </SwiperSlide>
      )}
    </Swiper>
  ) : (
    <>
      {viewGrid && isMobile ? (
        <div className="post__container--grid">
          {validPixleeData?.map((post) => (
            <img
              className={`img-fluid post-${post.id}`}
              src={post.pixlee_cdn_photos.square_medium_url}
              key={post.id}
              alt={post.alt_text}
              onClick={() => viewThePost(post.id)}
            />
          ))}
        </div>
      ) : (
        <div className="row no-gutters mt-lg-2 page-grid">
          {validPixleeData?.map((post, index) => (
            <PixleePost
              data={post}
              index={index}
              key={post.id}
              productsShopifyData={productsShopifyData}
              isPageLayout={isPageLayout}
            />
          ))}
        </div>
      )}
    </>
  );
};

PixleeFeedContainer = observer(PixleeFeedContainer);
export { PixleeFeedContainer };
